import React, { useState } from 'react';
import { motion } from 'framer-motion';
import useClickOutside from '../../../../../../hooks/useClickOutside';
import { Result } from '../../../../../../services/serpApi';
import { useTranslation } from 'react-i18next';

interface SelectAllDropdownProps {
  allResults: Result[]; // Este agora é o chunk filtrado
  selectedResults: number[]; 
  setSelectedResults: (ids: number[]) => void;
}

const SelectAllDropdown: React.FC<SelectAllDropdownProps> = ({
  allResults: filteredResults, // Renomeando para clareza
  selectedResults,
  setSelectedResults,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  const handleSelect = (type: 'all' | 'whatsapp_only' | 'whatsapp_unprospected' | 'prospected' | 'unprospected') => {
    let newSelection: number[] = [];

    switch (type) {
      case 'all':
        // Seleciona todos os resultados ATUALMENTE FILTRADOS
        newSelection = filteredResults.map(r => r.id);
        break;
      case 'whatsapp_only':
        newSelection = filteredResults.filter(r => r.whatsapp).map(r => r.id);
        break;
      case 'whatsapp_unprospected':
        newSelection = filteredResults.filter(r => r.whatsapp && !r.prospected).map(r => r.id);
        break;
      case 'prospected':
        newSelection = filteredResults.filter(r => r.prospected).map(r => r.id);
        break;
      case 'unprospected':
        newSelection = filteredResults.filter(r => !r.prospected).map(r => r.id);
        break;
    }

    // Se a seleção atual for idêntica à nova seleção, desmarque tudo.
    const currentSelectionSet = new Set(selectedResults);
    const newSelectionSet = new Set(newSelection);
    
    const isAlreadySelected = newSelection.length > 0 && newSelection.every(id => currentSelectionSet.has(id)) && currentSelectionSet.size === newSelectionSet.size;

    setSelectedResults(isAlreadySelected ? [] : newSelection);
    setIsOpen(false);
  };

  const whatsappResults = filteredResults.filter(r => r.whatsapp);
  const whatsappUnprospectedResults = filteredResults.filter(r => r.whatsapp && !r.prospected);
  const prospectedResults = filteredResults.filter(r => r.prospected);
  const unprospectedResults = filteredResults.filter(r => !r.prospected);

  const selectionOptions = [
    { 
      label: t('dashboard.select_option_all'), 
      type: 'all' as const, 
      count: filteredResults.length,
      action: () => handleSelect('all')
    },
    { 
      label: t('dashboard.select_option_whatsapp'), 
      type: 'whatsapp_only' as const, 
      count: whatsappResults.length,
      action: () => handleSelect('whatsapp_only')
    },
    { 
      label: t('dashboard.select_option_whatsapp_unprospected'), 
      type: 'whatsapp_unprospected' as const, 
      count: whatsappUnprospectedResults.length,
      action: () => handleSelect('whatsapp_unprospected')
    },
    { 
      label: t('dashboard.select_option_prospected'), 
      type: 'prospected' as const, 
      count: prospectedResults.length,
      action: () => handleSelect('prospected')
    },
    { 
      label: t('dashboard.select_option_unprospected'), 
      type: 'unprospected' as const, 
      count: unprospectedResults.length,
      action: () => handleSelect('unprospected')
    },
  ];
  
  // A seleção total agora é baseada no chunk filtrado
  const isAllSelected = selectedResults.length === filteredResults.length && filteredResults.length > 0;
  const isPartiallySelected = selectedResults.length > 0 && !isAllSelected;

  const buttonText = isAllSelected 
    ? t('dashboard.select_all_selected') 
    : isPartiallySelected 
      ? t('dashboard.select_partial', { count: selectedResults.length }) 
      : t('dashboard.select_all');

  return (
    <div className="relative flex items-center gap-2" ref={dropdownRef}>
      
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/50 hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {buttonText}
        <span className="material-symbols-outlined text-lg">
          expand_more
        </span>
      </motion.button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 origin-top-left"
        >
          <div className="p-2">
            {selectionOptions.map((option) => (
              <motion.button
                key={option.type}
                onClick={option.action}
                // Usando justify-start e padding para alinhar o texto
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                whileHover={{ x: 2 }}
              >
                <span className="text-left">{option.label}</span>
                <span className="text-xs font-semibold text-primary dark:text-orange-300">{option.count}</span>
              </motion.button>
            ))}
            {selectedResults.length > 0 && (
                <motion.button
                    onClick={() => {
                      setSelectedResults([]);
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center justify-center mt-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors border-t border-gray-100 dark:border-gray-700/50"
                    whileHover={{ scale: 1.01 }}
                >
                    {t('dashboard.clear_selection', { count: selectedResults.length })}
                </motion.button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SelectAllDropdown;